/**
 * RNG có seed cho core (core-loop 3.3, review s1 mục E3).
 *
 * Thuật toán chốt: mulberry32, state = 1 số u32 nằm trong state trận.
 * - `nextU32()` trả số nguyên 0..2^32-1.
 * - `nextInt(n) = floor(nextU32() * n / 2^32)`, 1 <= n <= 2^20. Tích < 2^53 nên phép tính
 *   trên double là chính xác. Mỗi lần gọi rút đúng 1 lần (không rejection sampling), để số
 *   lần rút chỉ phụ thuộc kết quả đã rút.
 * Vector cố định: test/rng.golden.json.
 */

/** Interface tối thiểu mà mọi hàm core dùng. Tiêm ScriptedRng để test thứ tự rút. */
export interface Rng {
  nextInt(n: number): number;
}

export const U32_RANGE = 4294967296; // 2^32
export const MAX_INT_BOUND = 1 << 20;

/** Chuẩn hoá seed tuỳ ý (số nguyên, có thể âm) về u32. */
export function toU32(seed: number): number {
  if (!Number.isInteger(seed)) throw new RangeError(`seed phải là số nguyên: ${seed}`);
  return seed >>> 0;
}

function checkBound(n: number): void {
  if (!Number.isInteger(n) || n < 1 || n > MAX_INT_BOUND) {
    throw new RangeError(`nextInt(n): n phải là số nguyên trong [1, ${MAX_INT_BOUND}], nhận ${n}`);
  }
}

export class Mulberry32 implements Rng {
  #state: number;

  constructor(seed: number) {
    this.#state = toU32(seed);
  }

  /** State u32 hiện tại (serialize/hash được). `new Mulberry32(r.state)` tiếp tục đúng chuỗi. */
  get state(): number {
    return this.#state;
  }

  nextU32(): number {
    this.#state = (this.#state + 0x6d2b79f5) >>> 0;
    let t = this.#state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (t ^ (t >>> 14)) >>> 0;
  }

  nextInt(n: number): number {
    checkBound(n);
    return Math.floor((this.nextU32() * n) / U32_RANGE);
  }

  clone(): Mulberry32 {
    return new Mulberry32(this.#state);
  }
}

/** Một lượt rút mong đợi trong ScriptedRng: `n` kỳ vọng và giá trị trả về. */
export type ScriptedDraw = readonly [n: number, value: number];

/**
 * RNG kịch bản cho golden test: mỗi `nextInt(n)` lấy phần tử đầu hàng đợi, assert `n` khớp,
 * trả `value`. Kiểm cùng lúc thứ tự rút và độ dài danh sách ứng viên.
 */
export class ScriptedRng implements Rng {
  readonly #queue: ScriptedDraw[];
  #calls = 0;

  constructor(draws: readonly ScriptedDraw[]) {
    this.#queue = draws.map(([n, v]) => [n, v] as const);
  }

  nextInt(n: number): number {
    checkBound(n);
    const next = this.#queue.shift();
    this.#calls++;
    if (!next) throw new Error(`ScriptedRng: hết kịch bản ở lần rút thứ ${this.#calls} (n=${n})`);
    const [expectedN, value] = next;
    if (expectedN !== n) {
      throw new Error(`ScriptedRng: lần rút thứ ${this.#calls} mong n=${expectedN}, nhận n=${n}`);
    }
    if (!Number.isInteger(value) || value < 0 || value >= n) {
      throw new Error(`ScriptedRng: giá trị ${value} nằm ngoài [0, ${n})`);
    }
    return value;
  }

  /** Số lượt còn lại chưa rút (test assert = 0 để chắc không rút thiếu). */
  get remaining(): number {
    return this.#queue.length;
  }

  get calls(): number {
    return this.#calls;
  }
}

/**
 * Rút có trọng số (core-loop 3.3): `r = nextInt(tổng)`, duyệt khoá theo đúng thứ tự `keys`,
 * chọn khoá đầu tiên có tổng cộng dồn > r. Khoá trọng số 0 không bao giờ được chọn.
 */
export function weightedPick<K>(rng: Rng, keys: readonly K[], weightOf: (k: K) => number): K {
  let total = 0;
  for (const k of keys) {
    const w = weightOf(k);
    if (!Number.isInteger(w) || w < 0) throw new RangeError(`trọng số không hợp lệ: ${w}`);
    total += w;
  }
  if (total <= 0) throw new RangeError('weightedPick: tổng trọng số phải > 0');
  const r = rng.nextInt(total);
  let acc = 0;
  for (const k of keys) {
    acc += weightOf(k);
    if (acc > r) return k;
  }
  /* c8 ignore next */
  throw new Error('weightedPick: không tới được');
}
